import { FC } from "react";

import {
    CalendarIcon,
    CheckOutlinedIcon,
    GraduationCapIcon,
    StopIcon,
    StarIcon,
    TradesIcon,
} from "../../assets/img/icons";
import { GraduationCondition } from "../../services/graduationApi";
import styles from "./styles.module.scss";

interface Props {
    conditions: GraduationCondition[];
}

const getConditionIcon = (conditionKey: string) => {
    switch (conditionKey) {
        case "sessions":
            return CalendarIcon;
        case "dayWin":
            return TradesIcon;
        case "funding":
            return StarIcon;
        case "role":
        case "tenure":
            return CheckOutlinedIcon;
        case "method":
            return GraduationCapIcon;
        default:
            return StopIcon;
    }
};

const GraduationConditionsList: FC<Props> = ({ conditions }) => {
    return (
        <div className={styles.section}>
            <div className={styles.conditionsTableCard}>
                <div className={styles.conditionsTitleRow}>
                    <h3 className={styles.sectionTitle}>Mon trading en quelques points</h3>
                    <span className={styles.conditionsCountBadge}>
                        {conditions.length} critère
                        {conditions.length > 1 ? "s" : ""} évalué
                        {conditions.length > 1 ? "s" : ""}
                    </span>
                </div>
                <div className={styles.conditionsTitleSeparator} />
                <div className={styles.conditionsTableHeader}>
                    <span className={styles.thCriterion}>CRITÈRES</span>
                    <span className={styles.thSituation}>VOTRE SITUATION</span>
                    <span className={styles.thStatus}>STATUT</span>
                </div>

                <div className={styles.conditionsRows}>
                    {conditions.map((condition) => {
                        const safeProgress = Math.max(
                            0,
                            Math.min(100, condition.progressPercent),
                        );
                        const isPending =
                            Boolean(condition.isPending) ||
                            condition.statusLabel === "Promotion non lancée";
                        const isInformative =
                            !isPending &&
                            (condition.key === "method" ||
                                condition.statusLabel === "Informatif");
                        const Icon = getConditionIcon(condition.key);
                        const progressClassName = isPending
                            ? styles.rowProgressFillPending
                            : isInformative
                              ? styles.rowProgressFillInfo
                              : condition.isMet
                                ? styles.rowProgressFillGreen
                                : safeProgress < 50
                                  ? styles.rowProgressFillRed
                                  : styles.rowProgressFillOrange;
                        const progressPercentClassName = isPending
                            ? styles.rowProgressPercentPending
                            : isInformative
                              ? styles.rowProgressPercentInfo
                              : condition.isMet
                                ? styles.rowProgressPercentGreen
                                : safeProgress < 50
                                  ? styles.rowProgressPercentRed
                                  : styles.rowProgressPercentOrange;
                        const rowIconStateClassName = isPending
                            ? styles.rowIconPending
                            : isInformative
                              ? styles.rowIconInformative
                              : condition.isMet
                                ? styles.rowIconValidated
                                : styles.rowIconNotValidated;
                        const rowStatusToneClassName = isPending
                            ? styles.rowMetaTextPending
                            : isInformative
                              ? styles.rowMetaTextInfo
                              : condition.isMet
                                ? styles.rowMetaTextGreen
                                : safeProgress < 50
                                  ? styles.rowMetaTextRed
                                  : styles.rowMetaTextOrange;
                        const rowStatusClassName = isPending
                            ? styles.statusPending
                            : isInformative
                              ? styles.statusInformative
                              : condition.isMet
                                ? styles.statusOk
                                : styles.statusNotOk;
                        const rowStatusLabel =
                            condition.statusLabel ??
                            (isPending
                                ? "Promotion non lancée"
                                : isInformative
                                  ? "Informatif"
                                  : condition.isMet
                                    ? "Validé"
                                    : "Non validé");

                        return (
                            <div
                                key={condition.key}
                                className={`${styles.conditionRow}${
                                    isPending ? ` ${styles.conditionRowPending}` : ""
                                }`}
                            >
                                <div className={styles.rowCriterion}>
                                    <span
                                        className={`${styles.rowIcon} ${rowIconStateClassName}`}
                                    >
                                        <Icon width={22} height={22} />
                                    </span>
                                    <div className={styles.rowCriterionText}>
                                        <div className={styles.rowCriterionLabel}>
                                            {condition.label}
                                        </div>
                                        {condition.description && (
                                            <div className={styles.rowCriterionDescription}>
                                                {condition.description}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.rowSituation}>
                                    {condition.gaugeCaption && (
                                        <div className={styles.rowGaugeCaption}>
                                            {condition.gaugeCaption}
                                        </div>
                                    )}
                                    <div className={styles.rowSituationTop}>
                                        <span
                                            className={`${styles.rowSituationValue} ${rowStatusToneClassName}`}
                                        >
                                            {isInformative
                                                ? ""
                                                : condition.currentValueLabel}
                                        </span>
                                        <span
                                            className={`${styles.rowProgressPercent} ${progressPercentClassName}`}
                                        >
                                            {isPending || isInformative
                                                ? isInformative
                                                    ? condition.currentValueLabel
                                                    : ""
                                                : condition.isMet
                                                  ? ""
                                                  : `${safeProgress}%`}
                                        </span>
                                    </div>
                                    <div className={styles.rowProgressTrack} aria-hidden>
                                        <div
                                            className={`${styles.rowProgressFill} ${progressClassName}`}
                                            style={{
                                                width: `${isPending ? 0 : safeProgress}%`,
                                            }}
                                        />
                                    </div>
                                    <div
                                        className={`${styles.rowTargetText} ${rowStatusToneClassName}`}
                                    >
                                        Objectif: {condition.targetValueLabel}
                                    </div>

                                    {condition.isBlocking === false && !isPending && (
                                        <div className={styles.rowInfoBadge}>
                                            Info uniquement
                                        </div>
                                    )}
                                </div>

                                <div className={styles.rowStatus}>
                                    <span className={rowStatusClassName}>
                                        {rowStatusLabel}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default GraduationConditionsList;
